/* eslint-disable jira/filename-case */

import type {
	NewIconType,
	ActionApi,
	IconType,
	ReferenceType,
	StatusType,
	StatusTypeResponse,
} from './ActionApi.types.tsx';
import { addIcon } from './addIcon.tsx';
import { createReferenceType } from './createReferenceType.tsx';
import { createStatusType } from './createStatusType.tsx';
import { deleteIcon } from './deleteIcon.tsx';
import { deleteReferenceType } from './deleteReferenceType.tsx';
import { deleteStatusType } from './deleteStatusType.tsx';
import { fetchIsDataManagerAvailable } from './fetchIsDataManagerAvailable.tsx';
import { fetchGlobalConfig } from './fetchGlobalConfig.tsx';
import { fetchMyDataManagerRoles } from './fetchMyDataManagerRoles.tsx';
import { fetchPrincipalsForRoles } from './fetchPrincipalsForRoles.tsx';
import { fetchProvisioningStatus } from './fetchProvisioningStatus.tsx';
import { fetchDataManagerTokens } from './fetchDataManagerTokens.tsx';
import { fetchCdm } from './fetchCdm.tsx';
import { installCdm } from './installCdm.tsx';

import { updateDataManagerRole } from './updateDataManagerRole.tsx';
import { setIcon, updateIcon, type RequestBodyType } from './updateIcon.tsx';
import { updateProvisioningStatus } from './updateProvisioningStatus.tsx';
import {
	setReferenceType,
	updateReferenceType,
	type RequestBodyType as RequestBodyTypeUpdateReferenceType,
} from './updateReferenceType.tsx';
import {
	setStatusType,
	updateStatusType,
	type RequestBodyType as RequestBodyTypeUpdateStatusType,
} from './updateStatusType.tsx';

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export type { IconProperties } from './updateIcon.tsx';
// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export type { ReferenceTypeProperties } from './updateReferenceType.tsx';
// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export type { StatusTypeProperties } from './updateStatusType.tsx';

export const actions: {
	readonly addIcon: (newIcon: NewIconType) => ActionApi;
	readonly createReferenceType: (data: {
		displayName?: string;
		name: string;
		description: string;
		color: string;
	}) => ActionApi;
	readonly createStatusType: (data: {
		name: string;
		description: string;
		category: string;
	}) => ActionApi;
	readonly deleteIcon: (icon: IconType) => ActionApi;
	readonly deleteReferenceType: (referenceType: ReferenceType) => ActionApi;
	readonly deleteStatusType: (statusType: StatusType) => ActionApi;
	readonly fetchCdm: () => ActionApi;
	readonly fetchDataManagerTokens: () => ActionApi;
	readonly fetchGlobalConfig: (shouldRefetch?: boolean) => ActionApi;
	readonly fetchIsDataManagerAvailable: () => ActionApi;
	readonly fetchMyDataManagerRoles: (shouldRefetch?: boolean) => ActionApi;
	readonly fetchPrincipalsForRoles: (roleIds: string[]) => ActionApi;
	readonly fetchProvisioningStatus: (shouldRefetch?: boolean) => ActionApi;
	readonly installCdm: () => ActionApi;
	readonly setIcon: (updatedIcon: IconType) => ActionApi;
	readonly setReferenceType: (updatedReferenceType: ReferenceType) => ActionApi;
	readonly setStatusType: (updatedStatusType: StatusTypeResponse) => ActionApi;
	readonly updateDataManagerRole: ({
		principalIds,
		roleId,
	}: {
		principalIds: string[];
		roleId: string;
	}) => ActionApi;
	readonly updateIcon: ({ iconId, updatedProperties }: RequestBodyType) => ActionApi;
	readonly updateProvisioningStatus: ({ isEnabled }: { isEnabled: boolean }) => ActionApi;
	readonly updateReferenceType: ({
		referenceTypeId,
		updatedProperties,
	}: RequestBodyTypeUpdateReferenceType) => ActionApi;
	readonly updateStatusType: ({
		statusTypeId,
		updatedProperties,
	}: RequestBodyTypeUpdateStatusType) => ActionApi;
} = {
	addIcon,
	createReferenceType,
	createStatusType,
	deleteIcon,
	deleteReferenceType,
	deleteStatusType,
	fetchCdm,
	fetchDataManagerTokens,
	fetchGlobalConfig,
	fetchIsDataManagerAvailable,
	fetchMyDataManagerRoles,
	fetchPrincipalsForRoles,
	fetchProvisioningStatus,
	installCdm,
	setIcon,
	setReferenceType,
	setStatusType,
	updateDataManagerRole,
	updateIcon,
	updateProvisioningStatus,
	updateReferenceType,
	updateStatusType,
} as const;

export type Actions = typeof actions;
